import type { Task } from '@obv/api-client';
import { EVERYONE, FAMILY, RECURRENCE_LABEL, dayLabel, groupTasks } from '@obv/core';
import { personColor } from '@obv/ui';
import { useRef, type ReactNode } from 'react';
import { newMutationKey } from '@/queries/hooks';
import { useRollover, useUpdateTask } from '@/queries/chores';
import { useChoreData } from '../choreData';
import { memberLabel, useDisplay } from '../context';
import { useDisplayUi } from '../store';
import { Avatar, Button, Card, CheckCircle, Chip, ScreenHeader, Unavailable } from '../ui';

const LONG_PRESS_MS = 550;

export function TasksScreen() {
  const ctx = useDisplay();
  const { filter, setFilter, openSheet } = useDisplayUi();
  const { tasks, completedOn } = useChoreData();
  const update = useUpdateTask();
  const rollover = useRollover();
  const today = ctx.clock.dateKey;
  const portrait = ctx.orientation === 'portrait';
  const g = tasks.data ? groupTasks(tasks.data.items, today, filter, completedOn) : null;
  const openToday = g ? g.today.length + g.overdue.length : 0;

  const toggle = (t: Task) => {
    if (t.id.indexOf('tmp-') === 0) return;
    update.mutate({ id: t.id, body: { completed: !t.completed }, key: newMutationKey() });
  };
  const edit = (t: Task) => t.id.indexOf('tmp-') !== 0 && openSheet({ mode: 'editTask', taskId: t.id });
  const chips = [{ id: EVERYONE, label: 'Everyone' }, ...ctx.boot.members.map((m) => ({ id: m.id, label: m.display_name })), { id: FAMILY, label: 'Family' }];
  const empty = g && !g.overdue.length && !g.today.length && !g.upcoming.length && !g.someday.length && !g.doneToday.length;

  return (
    <>
      <ScreenHeader
        eyebrow="Chores & to-dos"
        title="Many hands, light work."
        right={
          <Button variant="primary" onClick={() => openSheet({ mode: 'addTask' })}>
            + Add a chore
          </Button>
        }
      />
      <div className="mt-4 flex flex-none flex-wrap">
        {chips.map((c) => (
          <div key={c.id} className="mb-2 mr-2">
            <Chip selected={filter === c.id} onClick={() => setFilter(c.id)}>
              {c.label}
            </Chip>
          </div>
        ))}
      </div>
      <div
        className="mt-2 grid min-h-0 flex-1"
        style={{ gridTemplateColumns: portrait ? 'minmax(0,1fr)' : 'minmax(0,1fr) 250px', gridTemplateRows: portrait ? 'minmax(0,1fr) auto' : 'minmax(0,1fr)', gridGap: 14 }}
      >
        <Card tone={5} className="flex flex-col overflow-hidden">
          <div className="scroll-y flex-1 px-5 pb-3 pt-1">
            {tasks.isLoading ? (
              <Unavailable>Loading chores…</Unavailable>
            ) : tasks.isError && !tasks.data ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="text-[15px]">Couldn’t load chores.</div>
                <Button className="mt-3" onClick={() => tasks.refetch()}>
                  Try again
                </Button>
              </div>
            ) : empty ? (
              <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
                <div className="font-serif text-[24px]">All clear.</div>
                <div className="mt-2 text-[14px] text-muted">Nothing to do. Add a chore when something comes up.</div>
              </div>
            ) : g ? (
              <>
                {g.overdue.length ? (
                  <Section title="Overdue" tone="warn">
                    {g.overdue.map((t) => (
                      <TaskRow key={t.id} task={t} note={dayLabel(t.due_date!, today)} onToggle={() => toggle(t)} onEdit={() => edit(t)} />
                    ))}
                  </Section>
                ) : null}
                <Section title="Today">
                  {g.today.length ? (
                    g.today.map((t) => <TaskRow key={t.id} task={t} onToggle={() => toggle(t)} onEdit={() => edit(t)} />)
                  ) : (
                    <div className="py-3 text-[14px] text-muted">Nothing due today.</div>
                  )}
                </Section>
                {g.upcoming.slice(0, 7).map((u) => (
                  <Section key={u.date} title={dayLabel(u.date, today)}>
                    {u.tasks.map((t) => (
                      <TaskRow key={t.id} task={t} onToggle={() => toggle(t)} onEdit={() => edit(t)} />
                    ))}
                  </Section>
                ))}
                {g.someday.length ? (
                  <Section title="Someday">
                    {g.someday.map((t) => (
                      <TaskRow key={t.id} task={t} onToggle={() => toggle(t)} onEdit={() => edit(t)} />
                    ))}
                  </Section>
                ) : null}
                {g.doneToday.length ? (
                  <Section title="Done today">
                    {g.doneToday.map((t) => (
                      <TaskRow key={t.id} task={t} onToggle={() => toggle(t)} onEdit={() => edit(t)} />
                    ))}
                  </Section>
                ) : null}
              </>
            ) : null}
          </div>
        </Card>

        <div className="flex min-h-0" style={{ flexDirection: portrait ? 'row' : 'column' }}>
          <Card tone={6} className="px-5 py-[18px]" style={{ flex: portrait ? 1 : 'none' }}>
            <div className="text-[12px] font-semibold tracking-eyebrow text-muted">TODAY</div>
            <div className="mt-[6px] font-serif text-[30px]">{openToday ? `${openToday} to do` : 'All done'}</div>
            <div className="mt-[2px] text-[13px] text-muted">
              {g ? `${g.doneToday.length} done today · tap a chore to tick it off` : ''}
            </div>
          </Card>
          <Card tone={7} className="flex flex-1 flex-col px-5 py-[18px]" style={portrait ? { marginLeft: 14 } : { marginTop: 14 }}>
            {g && g.overdue.length ? (
              <>
                <div className="text-[16px]">
                  {g.overdue.length} overdue {g.overdue.length === 1 ? 'chore' : 'chores'}
                </div>
                <div className="mt-[3px] text-[13px] text-muted">Bring them forward to today so nothing slips.</div>
                <Button className="mt-4 self-start" onClick={() => rollover.mutate({ to: today, key: newMutationKey() })} disabled={rollover.isLoading}>
                  Move to today
                </Button>
              </>
            ) : (
              <>
                <div className="text-[16px]">Repeating chores</div>
                <div className="mt-[3px] text-[13px] leading-[1.45] text-muted">
                  Tick off a repeating chore and the next one appears on its own — daily, on weekdays, or every week.
                </div>
              </>
            )}
            <div className="mt-auto pt-3 text-[12px] text-muted">Hold a chore to edit it.</div>
          </Card>
        </div>
      </div>
    </>
  );
}

function Section({ title, tone, children }: { title: string; tone?: 'warn'; children: ReactNode }) {
  return (
    <div className="pt-3">
      <div className="pb-1 text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: tone === 'warn' ? 'var(--now)' : 'var(--muted)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function TaskRow({ task, note, onToggle, onEdit }: { task: Task; note?: string; onToggle: () => void; onEdit: () => void }) {
  const ctx = useDisplay();
  const who = memberLabel(ctx, task.member_id);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);
  const start = () => {
    held.current = false;
    timer.current = setTimeout(() => {
      held.current = true;
      onEdit();
    }, LONG_PRESS_MS);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const meta = [note, task.recurrence !== 'none' ? `↻ ${RECURRENCE_LABEL[task.recurrence]}` : null].filter(Boolean).join(' · ');
  return (
    <div className="flex min-h-[52px] items-center border-b" style={{ borderColor: 'var(--line2)', opacity: task.completed ? 0.5 : 1 }}>
      <CheckCircle checked={task.completed} onClick={onToggle} label={`${task.title}${task.completed ? ', done' : ''}`} color={personColor(who.colorKey)} />
      <button
        type="button"
        className="tap ml-2 flex min-w-0 flex-1 items-center py-2 text-left"
        onTouchStart={start}
        onTouchEnd={cancel}
        onTouchMove={cancel}
        onMouseDown={start}
        onMouseUp={cancel}
        onMouseLeave={cancel}
        onClick={() => {
          if (!held.current) onToggle();
        }}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={`${task.title}. Tap to ${task.completed ? 'undo' : 'tick off'}, hold to edit.`}
      >
        <span className="min-w-0 flex-1">
          <span className="ellipsis block text-[16px]" style={{ textDecoration: task.completed ? 'line-through' : 'none' }}>
            {task.priority === 'high' && !task.completed ? (
              <span className="mr-[6px] font-semibold" style={{ color: 'var(--now)' }} aria-label="High priority">
                !
              </span>
            ) : null}
            {task.title}
          </span>
          {meta ? <span className="mt-[2px] block text-[12px] text-muted">{meta}</span> : null}
        </span>
        <span className="ml-3 flex flex-none items-center text-[12px] text-muted2">
          <Avatar initial={who.initial} />
          <span className="ml-[7px]">{who.name}</span>
        </span>
      </button>
    </div>
  );
}
